import {
  ACCESSIBILITY_ARGS,
  ACTION_ARGS,
  Arg,
  COMPONENT_STATE_ARGS,
  INPUT_FIELD_ARGS,
  INPUT_FORM_ARGS,
  INPUT_GROUP_ARGS,
  Metadata,
  pick,
} from '../../../../utils/spds/metadata';
import { fn } from '@ember/helper';
import { reads } from 'macro-decorators';
import { tracked } from '@glimmer/tracking';
import FlexRow from '../../layout/flex/row.gjs';
import InputBase from '../../core/input.gjs';
import Super from '../../core/base.gjs';
import styles from './base.module.scss';

const METADATA = Metadata.define({
  description: 'Labeled input container; wraps core input with label and forwards args.',
  extends: [
    pick(COMPONENT_STATE_ARGS, ['autofocus', 'disabled', 'error']),
    pick(ACTION_ARGS, [
      'onBlur',
      'onChange',
      'onClick',
      'onFocus',
      'onInput',
      'onKeyDown',
      'onKeyUp',
    ]),
    pick(ACCESSIBILITY_ARGS, ['ariaLabel']),
    pick(INPUT_FIELD_ARGS, [
      { name: 'type', required: true },
      'checked',
      'indeterminate',
      'inputClasses',
      'maxLength',
      'component',
    ]),
    pick(INPUT_FORM_ARGS, ['name', 'placeholder', 'required', 'value']),
    pick(INPUT_GROUP_ARGS, ['groupName']),
  ],
  meta: {
    additionalArguments: Arg.array({
      description: 'Extra validation rules from parent component',
    }),
  },
  name: 'Labeled Base',
});

export default class SpdsInputLabeledBase extends Super {
  static metadata = this.setMetadata(METADATA, import.meta.url);

  baseClass = 'spds-input-container spds-input-labeled';
  classesToMap = ['checked', 'disabled', 'error', 'indeterminate', 'required'];
  styles = styles;

  @tracked inputComponent;

  @reads('args.value') value;

  get inputElement() {
    return this.inputComponent?.baseElement;
  }

  get labelId() {
    return this.composeSubcomponentId('label');
  }

  <template>
    <FlexRow
      @alignItems="center"
      @flex="1"
      @flexWidth={{false}}
      @gap="6px"
      @inputContainer={{true}}
      @tag="label"
      class={{this.classes}}
      data-test-spds-input-label={{this.labelId}}
      id={{this.labelId}}
      for={{this.id}}
      {{this.validateArguments}}
      ...attributes
    >
      <InputBase
        @additionalArguments={{@additionalArguments}}
        @ariaLabel={{@ariaLabel}}
        @autofocus={{@autofocus}}
        @checked={{@checked}}
        @component={{@component}}
        @disabled={{@disabled}}
        @error={{@error}}
        @groupName={{@groupName}}
        @id={{this.id}}
        @indeterminate={{@indeterminate}}
        @maxLength={{@maxLength}}
        @onBlur={{@onBlur}}
        @onChange={{@onChange}}
        @onClick={{@onClick}}
        @onFocus={{@onFocus}}
        @onInput={{@onInput}}
        @onKeyDown={{@onKeyDown}}
        @onKeyUp={{@onKeyUp}}
        @name={{@name}}
        @placeholder={{@placeholder}}
        @registerComponent={{fn this.registerComponent "inputComponent"}}
        @required={{@required}}
        @type={{@type}}
        @unregisterComponent={{fn this.unregisterComponent "inputComponent"}}
        @value={{@value}}
        class={{@inputClasses}}
      />
      {{yield}}
    </FlexRow>
  </template>
}
